package dev.lingvo.site.state

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.site.api.Session
import kotlinx.browser.window
import org.w3c.dom.StorageEvent

/**
 * Which workspace the settings pages are about.
 *
 * Every workspace surface needs an answer to this, and picking "the strongest membership" is right
 * for the overwhelmingly common case of one workspace and wrong the moment someone belongs to two —
 * where a page silently showing the *other* team's members and invoices is worse than showing
 * nothing.
 *
 * Remembered across reloads because the alternative is a contractor who works in one workspace
 * re-selecting it every single time they open settings. Held in local storage rather than the URL so
 * a link someone pastes into a chat does not silently switch the recipient's workspace.
 */
object WorkspaceSelection {

    private var stored: String? = read()

    /** The slug the user last chose, if they are still a member of it. */
    var selectedSlug: String? by mutableStateOf(stored)
        private set

    /**
     * A slug another tab chose, which this page is not showing.
     *
     * Null whenever this tab and local storage agree, which is almost always.
     *
     * **This is the mitigation the "one selection for all tabs" decision was accepted on.** Holding
     * the choice in local storage rather than the URL means two tabs share one selection, so
     * switching workspace in one silently changes what the other's *next* action applies to — while
     * that other tab carries on rendering the old workspace's name, and possibly a half-typed form.
     * The cost was accepted on the strength of telling the user, and then nobody told the user.
     *
     * Never acted on automatically. A reload here discards typed input, which is the failure the
     * whole arrangement exists to avoid — so this is surfaced and the person decides.
     */
    var incomingSlug: String? by mutableStateOf(null)
        private set

    init {
        // The listener lives with the state it feeds rather than in a shell, so the signal cannot be
        // installed against one selection while a different one is read. `storage` fires only in the
        // *other* tabs of an origin, never the one that wrote — which is exactly the semantics
        // wanted here, and the reason the writing tab needs no suppression of its own.
        runCatching {
            window.addEventListener("storage", { event ->
                val storage = event as? StorageEvent
                if (storage?.key == KEY) {
                    val incoming = storage.newValue?.takeIf(String::isNotBlank)
                    incomingSlug = incoming?.takeIf { it != selectedSlug }
                }
            })
        }
    }

    /**
     * The workspace to act on.
     *
     * Falls back to the strongest membership when nothing is chosen or the choice is stale — someone
     * removed from a workspace must not be left on a page that will 404 for ever.
     */
    fun current(): WorkspaceDto? {
        val workspaces = Session.organizations
        return workspaces.firstOrNull { it.slug == selectedSlug }
            ?: workspaces.minByOrNull { it.role.ordinal }
    }

    fun select(slug: String) {
        selectedSlug = slug
        stored = slug
        // Clearing here rather than in the banner, because two callers already change the selection
        // and a third would have to remember. Once this tab writes, local storage holds this tab's
        // choice: there is nothing left to reload for, and a bar still offering to would name a
        // workspace the reload will not land on.
        incomingSlug = null
        runCatching { window.localStorage.setItem(KEY, slug) }
    }

    private fun read(): String? =
        runCatching { window.localStorage.getItem(KEY) }.getOrNull()?.takeIf(String::isNotBlank)

    private const val KEY = "lingvo:workspace"
}
